declare global {
  interface Window {
    __FLOWGEN__?: { apiBase?: string; mediaBase?: string };
  }
}

function resolveApiBase(): string {
  // Prefer non-empty runtime config (from build or serve.mjs). Ignore "".
  const runtime = typeof window !== "undefined" ? window.__FLOWGEN__?.apiBase?.trim() : "";
  if (runtime) return runtime.replace(/\/+$/, "");
  const baked = (import.meta.env.VITE_API_BASE as string | undefined)?.trim();
  if (baked) return baked.replace(/\/+$/, "");
  return "/api";
}

function resolveMediaBase(apiBase: string): string {
  const runtime = typeof window !== "undefined" ? window.__FLOWGEN__?.mediaBase?.trim() : "";
  if (runtime) return runtime.replace(/\/+$/, "");
  const baked = (import.meta.env.VITE_MEDIA_BASE as string | undefined)?.trim();
  if (baked) return baked.replace(/\/+$/, "");
  return deriveMediaBase(apiBase);
}

function deriveMediaBase(apiBase: string): string {
  if (apiBase.startsWith("http")) {
    return apiBase.replace(/\/api\/?$/, "").replace(/\/+$/, "") + "/media";
  }
  return "/media";
}

const API_BASE = resolveApiBase();
const MEDIA_BASE = resolveMediaBase(API_BASE);

const PROJECT_STORAGE_KEY = "flowgen.activeProjectId";
const TOKEN_STORAGE_KEY = "flowgen.sessionToken";

/** Routes that do not require X-Project-Id. */
const UNSCOPED_PREFIXES = [
  "/projects",
  "/config",
  "/logs",
  "/health",
  "/auth",
];

function isUnscoped(path: string): boolean {
  return UNSCOPED_PREFIXES.some((p) => path === p || path.startsWith(p + "/"));
}

export interface ApiError {
  status: number;
  message: string;
  details?: unknown;
}

async function parseBody(res: Response): Promise<any> {
  const text = await res.text();
  if (!text) return undefined;
  try {
    return JSON.parse(text);
  } catch {
    if (text.trimStart().toLowerCase().startsWith("<!doctype") || text.trimStart().startsWith("<")) {
      const err: ApiError = {
        status: res.status,
        message: "api_html_response",
        details: { apiBase: API_BASE },
      };
      throw err;
    }
    const err: ApiError = {
      status: res.status,
      message: "invalid_json_response",
      details: { preview: text.slice(0, 120) },
    };
    throw err;
  }
}

class ApiClient {
  private projectId: string | null = null;
  private token: string | null = null;

  constructor() {
    try {
      this.projectId = localStorage.getItem(PROJECT_STORAGE_KEY);
      this.token = localStorage.getItem(TOKEN_STORAGE_KEY);
    } catch {
      this.projectId = null;
      this.token = null;
    }
  }

  getActiveProjectId(): string | null {
    return this.projectId;
  }

  setActiveProjectId(id: string | null): void {
    this.projectId = id;
    try {
      if (id) localStorage.setItem(PROJECT_STORAGE_KEY, id);
      else localStorage.removeItem(PROJECT_STORAGE_KEY);
    } catch {
      /* ignore */
    }
  }

  getToken(): string | null {
    return this.token;
  }

  setToken(token: string | null): void {
    this.token = token;
    try {
      if (token) localStorage.setItem(TOKEN_STORAGE_KEY, token);
      else localStorage.removeItem(TOKEN_STORAGE_KEY);
    } catch {
      /* ignore */
    }
  }

  projectHeaders(): Record<string, string> {
    const headers: Record<string, string> = {};
    if (this.projectId) headers["X-Project-Id"] = this.projectId;
    if (this.token) headers.Authorization = `Bearer ${this.token}`;
    return headers;
  }

  private async request<T>(method: string, path: string, body?: unknown): Promise<T> {
    const headers: Record<string, string> = {};
    if (body) headers["Content-Type"] = "application/json";
    if (this.token) headers.Authorization = `Bearer ${this.token}`;
    if (!isUnscoped(path) && this.projectId) {
      headers["X-Project-Id"] = this.projectId;
    }
    const res = await fetch(`${API_BASE}${path}`, {
      method,
      headers: Object.keys(headers).length ? headers : undefined,
      body: body ? JSON.stringify(body) : undefined,
    });
    if (!res.ok) {
      let details: any = undefined;
      try {
        details = await parseBody(res);
      } catch (err: any) {
        if (err?.message === "api_html_response" || err?.message === "invalid_json_response") throw err;
      }
      const err: ApiError = {
        status: res.status,
        message: details?.error ?? `Request failed (${res.status})`,
        details,
      };
      throw err;
    }
    if (res.status === 204) return undefined as T;
    return parseBody(res) as Promise<T>;
  }

  get<T>(path: string) { return this.request<T>("GET", path); }
  post<T>(path: string, body?: unknown) { return this.request<T>("POST", path, body); }
  put<T>(path: string, body?: unknown) { return this.request<T>("PUT", path, body); }
  patch<T>(path: string, body?: unknown) { return this.request<T>("PATCH", path, body); }
  delete<T>(path: string) { return this.request<T>("DELETE", path); }

  mediaUrl(rel: string | undefined | null): string {
    if (!rel) return "";
    if (rel.startsWith("http")) return rel;
    if (rel.startsWith("/")) return `${MEDIA_BASE}${rel.replace(/^\/media/, "")}`;
    return `${MEDIA_BASE}/${rel}`;
  }
}

export const api = new ApiClient();

export const API_BASE_URL = API_BASE;
export const MEDIA_BASE_URL = MEDIA_BASE;
